import React, { ReactElement, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { BsCheckCircle, BsFillCalendarDateFill } from "react-icons/bs";
import { FaUser } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { MdIndeterminateCheckBox } from "react-icons/md";
import TimeAgo from "react-timeago";
import { TicketTask, TicketTaskState, useSaveTicketStateStatusMutation } from "../../__generated__/api";

interface TicketTasksProps {
    tasks: TicketTask[];
}

function TicketTaskRow({ task }: {task: TicketTask}): ReactElement {
    const [save] = useSaveTicketStateStatusMutation();
    const [status, setStatus] = useState<TicketTaskState>(() => task.status);

    const onSetStatus = async (state: TicketTaskState) => {
        setStatus(state);
        await save({
            variables: {
                id: task.id,
                state,
            },
        });
    };

    const dueDate = task.dueDate ? new Date(task.dueDate) : undefined;
    const context = [];
    if (status === TicketTaskState.Open && !task.isAssignedToCurrentUser && task.assignee) {
        context.push(
            <small title={`Assigned to ${task.assignee.name}`}>
                <FaUser />
                {" "}
                {task.assignee.name}
            </small>,
        );
    }

    if (status === TicketTaskState.Open && dueDate) {
        const dueDateText = `${dueDate.toLocaleDateString()} ${dueDate.toLocaleTimeString()}`;
        context.push(
            <small title={`Due date: ${dueDateText}`}>
                <BsFillCalendarDateFill />
                {" "}
                <TimeAgo
                    date={dueDate}
                    minPeriod={60}
                    title={dueDateText}
                />
            </small>,
        );
    }

    return (
        <div className="list-group-item px-0 " key={task.id}>

            <div className="d-flex">
                {status === TicketTaskState.Open ? (
                    <Dropdown className="me-1" drop="end">
                        <Dropdown.Toggle
                            split
                            variant=""
                            className="px-2 py-0 my-auto rounded-end"
                        />
                        <Dropdown.Menu flip>
                            <Dropdown.Item onClick={() => onSetStatus(TicketTaskState.Finished)}>
                                Done
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => onSetStatus(TicketTaskState.Rejected)}>
                                Reject
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => onSetStatus(TicketTaskState.NotApplicable)}>
                                Not Applicable
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                ) : (
                    <div className="ms-0" />
                )}

                <div className="w-100">
                    <div className="d-flex justify-content-between">
                        <div>
                            {status === TicketTaskState.Finished && (
                                <BsCheckCircle className="text-success me-2" title="Done" />
                            )}
                            {status === TicketTaskState.Rejected && (
                                <IoMdClose className="text-danger me-2" title="Rejected" />
                            )}
                            {status === TicketTaskState.NotApplicable && (
                                <MdIndeterminateCheckBox className="text-gray-500 me-2" title="Not Applicable" />
                            )}
                            {task.name}
                        </div>
                        <div>
                            <div className="text-light d-flex flex-column">
                                {context.map(row => row)}
                            </div>
                        </div>

                    </div>
                    <small>{task.description}</small>

                </div>
            </div>

        </div>
    );
}

export default function TicketTasks({
    tasks,
}: TicketTasksProps): ReactElement {
    return (
        <div className="list-group list-group-flush border-bottom">
            {tasks.map(task => (
                <TicketTaskRow
                    task={task}
                    key={task.id}
                />
            ))}
        </div>
    );
}
